import { CircularProgressbar } from "react-circular-progressbar";
import { ScoreSection } from "@shared/features/newMarketplace/types";
import React, { CSSProperties, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import {
  SCORE_SECTION_TO_COLOR,
  SCORE_SECTION_TO_DARK_MODE_COLOR,
} from "@shared/features/newMarketplace/constants";
import { typography } from "@shared/common/Themes/typography";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice";
import { v4 as uuidv4 } from "uuid";

interface ScoreProgressChartProps {
  getSumSectionByKey: (
    section: ScoreSection,
    key: "weighted_score" | "weight"
  ) => number;
}

const ScoreProgressChart = ({
  getSumSectionByKey,
}: ScoreProgressChartProps) => {
  const theme = useTheme();
  const uuid = useMemo(() => uuidv4(), []);

  const getBaseStyles = (
    isTrail: boolean,
    section: ScoreSection
  ): CSSProperties => {
    let strokeColor = hexToRGB(theme.colors.secondaryGrey, 0.14);
    if (!isTrail) {
      strokeColor = `url(#${uuid}_${section})`;
    }
    return {
      strokeLinecap: "butt",
      strokeWidth: SECTION_TO_STROKE_WIDTH[section],
      stroke: strokeColor,
      transform: "rotate(-90deg)",
      transformOrigin: "center center",
    };
  };

  const getSectionHalfCircleProgressBar = (section: ScoreSection) => {
    const weightedScore = getSumSectionByKey(section, "weighted_score");
    const weight = getSumSectionByKey(section, "weight");

    return (
      <HalfCircleWrapper
        $size={SECTION_TO_CIRCLE_WIDTH[section]}
        $isOuter={section === ScoreSection.PERFORMANCE}
        key={`${section}_progress`}
      >
        <GradientSvg section={section} uuid={uuid} />
        <CircularProgressbar
          value={weight ? (weightedScore / weight) * 100 : 0}
          minValue={0}
          maxValue={100}
          circleRatio={0.5}
          styles={{
            path: getBaseStyles(false, section),
            trail: getBaseStyles(true, section),
          }}
        />
      </HalfCircleWrapper>
    );
  };

  const getTotalValue = (key: "weighted_score" | "weight") => {
    return Math.round(
      Object.values(ScoreSection).reduce(
        (sum, section) => sum + getSumSectionByKey(section, key),
        0
      )
    );
  };

  const total = (
    <Total>
      <Text type="secondary" $size={typography.fontSizes.S}>
        Total
      </Text>
      <Flex $alignItems="baseline" style={{ lineHeight: "30px" }}>
        <WeightedScore>{getTotalValue("weighted_score")}</WeightedScore>
        <Text type="secondary">
          <span style={{ marginRight: 1 }}>/</span>
          {getTotalValue("weight")}
        </Text>
      </Flex>
    </Total>
  );

  return (
    <Wrapper>
      {Object.values(ScoreSection).map((section) =>
        getSectionHalfCircleProgressBar(section)
      )}
      {total}
    </Wrapper>
  );
};

export default ScoreProgressChart;

interface GradientSvgProps {
  section: ScoreSection;
  uuid?: string;
}

const GradientSvg = ({ section, uuid }: GradientSvgProps) => {
  const isDarkMode = useSelector(selectIsDarkMode);

  return (
    <svg style={{ height: 0, width: 0 }}>
      <defs>
        <linearGradient id={`${uuid}_${section}`} x1="0" y1="0" x2="0" y2="1">
          {Object.entries(
            isDarkMode
              ? SCORE_SECTION_TO_DARK_MODE_COLOR[section]
              : SCORE_SECTION_TO_COLOR[section]
          ).map(([offset, color], index) => {
            return <stop key={index} offset={offset} stopColor={color} />;
          })}
        </linearGradient>
      </defs>
    </svg>
  );
};

const SECTION_TO_CIRCLE_WIDTH = {
  [ScoreSection.PERFORMANCE]: 220,
  [ScoreSection.RISK]: 196,
  [ScoreSection.SERVICE]: 172,
};

const SECTION_TO_STROKE_WIDTH = {
  [ScoreSection.PERFORMANCE]: 4,
  [ScoreSection.RISK]: 4.5,
  [ScoreSection.SERVICE]: 5,
};

const Wrapper = styled(Flex)`
  justify-content: center;
  position: relative;
  margin-bottom: 24px;
`;
const HalfCircleWrapper = styled.div<{ $size: number; $isOuter?: boolean }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size / 2}px;
  ${(p) =>
    !p.$isOuter &&
    `
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
  `};
`;
const Total = styled(Flex)`
  flex-direction: column;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
`;
const WeightedScore = styled(Title).attrs({ level: 2 })`
  color: ${(p) => p.theme.colors.primaryBlue};
  margin-right: 4px;
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  font-size: 24px;
`;
